<template>
    <component :is="Head">
        <html :lang="i18n.global.locale" />
        <title>{{ siteStore.site.name }}</title>
        <link rel="icon" type="image/x-icon" :href="siteStore.site.logoMobileUrl" />

        <body class="bg-background text-white antialiased" />
    </component>
    <div>
        <UiLoader v-if="isLoadingComposable" fullscreen opaque>{{ t('Loading') }}</UiLoader>
        <router-view v-else />
    </div>
    <UiDialog />
</template>

<script lang="ts" setup>
import { ConfirmationRoute } from './types/ConfirmationRoute';
import { useAppInit } from '@/composables/useAppInit';
const { initApp, isLoading: isLoadingComposable } = useAppInit();

const siteStore = useSiteStore();
const productStore = useProductStore();
const stageStore = useStageStore();
const orderApi = useOrder();
const route = useRoute();
const router = useRouter();
const { t } = i18n.global;
const { confirm } = useDialog();
const { askEcommerceCustomerId } = usePostMessage();

const isMobile = useMediaQuery(horizontalMediaQueries.mobile);
const { isInboundOriginAllowed } = useParentOrigin();
useEventListener(window, 'message', handleMessage);

onMounted(async () => {
    // Debug: Vérifier window.env au démarrage
    console.log('🔍 [App.vue] onMounted - window.env:', window.env);
    console.log('🔍 [App.vue] onMounted - window.env?.VITE_BACK_URL:', window.env?.VITE_BACK_URL);
    console.log('🔍 [App.vue] onMounted - window.location.href:', window.location.href);

    // Vérifier si env.js est chargé
    if (!window.env) {
        console.error('❌ [App.vue] window.env non disponible ! Vérifiez que env.js est chargé');
        console.log('🔍 [App.vue] Vérification des scripts dans le DOM:');
        const scripts = document.querySelectorAll('script');
        scripts.forEach((script, index) => {
            console.log(`  Script ${index}:`, script.src || 'inline');
        });
    }

    askEcommerceCustomerId();
    await router.isReady();
    stageStore.fonts = await loadFonts(window.env.VITE_GOOGLE_FONTS_API_KEY);

    if (route.query.save) {
        loadSaveFromRecordId(route.query.save as string);
    }

    if (route.query.hiddenCategories) {
        hideCategories(route.query.hiddenCategories as string);
    }
});

function hideCategories(hiddenCategoriesParam: string) {
    const hiddenCategories = hiddenCategoriesParam ? hiddenCategoriesParam.split(',') : [];
    useCategoryStore().hiddenCategories = hiddenCategories;
}

function handleMessage(message: MessageEvent<unknown>) {
    try {
        if (!isInboundOriginAllowed(message.origin)) {
            // In strict mode (parentOrigin set in URL), drop messages from any
            // other origin. In legacy mode, this is always true → no behavior
            // change for existing integrations.
            return;
        }
        if (typeof message.data !== 'string') {
            return;
        }

        const payload = message.data.trim();
        // Ignore non-JSON postMessage payloads (e.g. timers/workers from third-party libs like JSZip).
        if (!payload || (payload[0] !== '{' && payload[0] !== '[')) {
            return;
        }

        const data = JSON.parse(payload);
        if (data.eventType === 'sendVariantsData' && data.variantsData) {
            if (stageStore.expectedVariantType === 'product') {
                productStore.updateVariants(data.variantsData);
            } else if (stageStore.expectedVariantType === 'options') {
                siteStore.updateOrderOptionsWithPrices(data.variantsData);
            }
            stageStore.expectedVariantType = null;
        } else if (data.eventType === 'sendVariantsData' && data.error) {
            stageStore.expectedVariantType = null;
            console.error(data.error);
        } else if (data.eventType === 'sendCustomerId' && data.customerId) {
            stageStore.customerId = data.customerId;
        } else if (data.eventType === 'sendEcommerceProduct' && data.options && data.variants) {
            const ecommerceData = ref(data);
            const { processEcommerceData } = useProduct();
            processEcommerceData(ecommerceData);
            if (route.name === '//products/[productId]/' || !stageStore.configuration.productId) {
                return;
            }
            router.push({
                name: '//products/[productId]/',
                params: { productId: stageStore.configuration.productId },
                query: { visual: route.query.visual, skuTheme: route.query.skuTheme }
            });
        }
    } catch (error) {
        console.warn('[App] Ignored invalid postMessage payload', error);
    }
}

async function loadSaveFromRecordId(recordId: string) {
    const { data, error } = await orderApi.getSaveByRecordId(recordId);
    if (error.value) {
        return;
    }
    const metadata = data.value.save.metadata;
    const { productId } = metadata || {};
    const save = {
        json: JSON.stringify(data.value.save),
        mediaList: data.value.mediaList || []
    };
    stageStore.save = save;
    stageStore.configuration.selectedMethod = 'manual';
    router.push({ name: '//products/[productId]/customize', params: { productId } });
}

function getConfirmationRoutes(): ConfirmationRoute[] {
    const handleCallbackOnLeaveCustomize = () => {
        const categoryId = stageStore.configuration.categoryId;
        stageStore.resetChanges();
        if (!siteStore.isFeatureEnabled('Themes') && !defaultSiteConfiguration.isLayoutEnabled) {
            return { name: '//categories/[categoryId]', params: { categoryId: categoryId ?? '' } };
        }
    };
    const middlewares: ConfirmationRoute[] = [
        {
            from: '//products/[productId]/customize',
            to: '//products/[productId]/',
            options: {
                title: t('LeaveManager'),
                message: t('LeaveConfirm'),
                confirmMessage: t('Confirm'),
                closeMessage: t('Cancel')
            },
            callback: handleCallbackOnLeaveCustomize
        },
        {
            from: /^\/\/products\/\[[^/]+\]\/(?!customizationType\/?).*/, // Matches any route under //products/[productId]/ except customisationType
            to: '//categories/[categoryId]',
            options: {
                title: t('LeaveManager'),
                message: t('LeaveConfirm'),
                confirmMessage: t('Confirm'),
                closeMessage: t('Cancel')
            },
            callback: handleCallbackOnLeaveCustomize
        },
        {
            from: /^\/\/products\/\[[^/]+\]\/(?!customizationType\/?).*/, // Matches any route under //products/[productId]/ except customisationType
            to: '//',
            options: {
                title: t('LeaveManager'),
                message: t('LeaveConfirm'),
                confirmMessage: t('Confirm'),
                closeMessage: t('Cancel')
            },
            callback: handleCallbackOnLeaveCustomize
        }
    ];
    const conditionalMiddlewares = isMobile.value
        ? [
              // routes for mobile
              {
                  from: '//products/[productId]/customize',
                  to: '//categories/[categoryId]',
                  options: {
                      title: t('LeaveManager'),
                      message: t('LeaveConfirm'),
                      confirmMessage: t('Confirm'),
                      closeMessage: t('Cancel')
                  },
                  callback: () => {
                      stageStore.resetChanges();
                  }
              }
          ]
        : [
              // routes for desktop
          ];

    return [...middlewares, ...conditionalMiddlewares];
}

router.beforeEach(async (to, from, next) => {
    await initApp();
    const matchedRoute = getConfirmationRoutes().find((route) => {
        let fromMatched = false;
        let toMatched = false;

        if (route.from instanceof RegExp) {
            fromMatched = route.from.test(from.name); // Match dynamic patterns
        } else {
            fromMatched = route.from === from.name;
        }

        if (route.to instanceof RegExp) {
            toMatched = route.to.test(to.name); // Match dynamic patterns
        } else {
            toMatched = route.to === to.name;
        }

        return fromMatched && toMatched;
    });
    if (!matchedRoute) {
        return next();
    }
    if (await confirm(matchedRoute.options)) {
        const callbackResult = matchedRoute.callback?.() ?? true;
        return next(() => callbackResult);
    }
    return next(false);
});
</script>

<style lang="scss">
@import '@/styles/settings.scss';
</style>
